// --------------------------------------------------------------
// Created On: 2026-10-16
// Author: Zachary Thomas
//
// Last Modified: 2026-11-03
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useEffect } from "react";
import { useDndContext, useDraggable } from "@dnd-kit/core";
import DroppableArea from "src/components/DroppableArea/DroppableArea";
import SystemControlComponentGauge from "./SystemControlComponentGauge/SystemControlComponentGauge";
import SystemControlComponentTable from "./SystemControlComponentTable/SystemControlComponentTable";
import RemoteOperationModal from "./RemoteOperationModal/RemoteOperationModal";
import { GAUGE_COMPONENT_TYPE, GAUGE_TYPE_TANK_ROUND } from "src/constants/systemControl";
import styles from "./SystemControlComponentWrapper.module.scss";

// Wrapper component for system control component content.
export default function SystemControlComponentWrapper(props: Props): Component {
  const [beingEdited, setBeingEdited] = useState<boolean>(false);
  const [showRemoteOperationModal, setShowRemoteOperationModal] = useState<boolean>(false);
  const canDrag = props.inEditMode;
  const { active } = useDndContext();
  const {
    attributes: dragAttributes,
    listeners: dragListeners,
    setNodeRef: setDraggableNodeRef,
    transform,
    isDragging,
  } = useDraggable({ id: props.systemControlComponentId, disabled: !canDrag });
  const MAX_COMPONENT_NAME_LENGTH = 50;
  const firstAttribute = props.selectedAttributes.at(0);
  const gaugeType = props.gauge?.componentGaugeType ?? GAUGE_TYPE_TANK_ROUND;

  // If we are ever not in edit mode, switch back to not being edited.
  useEffect(() => {
    if (!props.inEditMode) {
      setBeingEdited(false);
    }
  }, [props.inEditMode]);

  return (
    <div className="col-12 col-md-6 col-lg-4 col-xl-3">
      <div className="d-flex h-100 position-relative">
        {props.inEditMode && <DroppableArea id={`before-${props.systemControlComponentId}`} isVertical />}
        <div className="flex-grow-1">
          <div
            data-test="system-control-component-wrapper"
            className={`${styles.body} ${isDragging ? styles.dragging : ""} ${
              active !== null ? styles.dragInProgress : ""
            }`}
            ref={setDraggableNodeRef}
            style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
          >
            <div className={styles.header}>
              <div className="row g-1">
                {canDrag && (
                  <div className="col-auto d-flex align-items-center">
                    <div
                      data-test="component-drag-handle"
                      className={styles.draggable}
                      title="Drag to reorder"
                      {...dragAttributes}
                      {...dragListeners}
                    >
                      <i className="fa fa-ellipsis-v" />
                      <i className="fa fa-ellipsis-v" />
                    </div>
                  </div>
                )}
                <div className="col d-flex align-items-center">
                  {beingEdited ? (
                    <input
                      data-test="component-name-input"
                      className="form-control"
                      maxLength={MAX_COMPONENT_NAME_LENGTH}
                      value={props.name}
                      onChange={(e) => props.onChangeComponentName(e.target.value)}
                    />
                  ) : (
                    <span className={styles.title}>{props.name}</span>
                  )}
                </div>

                {props.inEditMode && (
                  <div className="col-auto">
                    <button
                      data-test="component-edit-button"
                      type="button"
                      className={`btn ${beingEdited ? "btn-secondary" : "btn-primary"}`}
                      onClick={() => setBeingEdited((prev) => !prev)}
                    >
                      {beingEdited ? "Close" : "Edit"}
                    </button>
                  </div>
                )}

                {props.inEditMode && (
                  <div className="col-auto">
                    <button
                      data-test="component-delete-button"
                      type="button"
                      className="btn btn-danger"
                      onClick={() => props.onDelete()}
                    >
                      <i className="fa fa-fw fa-times fa-xs" />
                    </button>
                  </div>
                )}

                {!props.inEditMode && props.remoteOperationEnabled && (
                  <div className="col-auto">
                    <button
                      data-test="component-remote-operation-button"
                      type="button"
                      className={`btn btn-primary btn-sm ${styles.remoteButton}`}
                      onClick={() => setShowRemoteOperationModal(true)}
                    >
                      Remote Operation
                    </button>
                  </div>
                )}
              </div>
            </div>
            {firstAttribute !== undefined && props.componentType === GAUGE_COMPONENT_TYPE && !beingEdited ? (
              <SystemControlComponentGauge
                currentValue={firstAttribute.currentValue}
                unitShortName={firstAttribute.unitShortName}
                highlightingRules={firstAttribute.highlightingRules}
                gaugeType={gaugeType}
                min={props.gauge?.min ?? ""}
                max={props.gauge?.max ?? ""}
              />
            ) : (
              <SystemControlComponentTable
                componentType={props.componentType}
                gaugeType={gaugeType}
                gaugeMin={String(props.gauge?.min ?? "")}
                gaugeMax={String(props.gauge?.max ?? "")}
                assets={props.assets}
                attributes={props.attributes}
                enumerationMap={props.enumerationMap}
                assetName={props.assetName}
                remoteOperationEnabled={props.remoteOperationEnabled}
                selectedAttributes={props.selectedAttributes}
                beingEdited={beingEdited}
                onChangeComponentType={(componentType) => props.onChangeComponentType(componentType)}
                onChangeGaugeType={(gaugeType) => props.onChangeGaugeType(gaugeType)}
                onChangeGaugeMin={(min) => props.onChangeGaugeMin(min)}
                onChangeGaugeMax={(max) => props.onChangeGaugeMax(max)}
                onChangeAssetName={(assetName) => props.onChangeAssetName(assetName)}
                onToggleRemoteOperationEnabled={() => props.onToggleRemoteOperationEnabled()}
                onChangeAttribute={(componentAssetAttributeId, attributeName) =>
                  props.onChangeAttribute(componentAssetAttributeId, attributeName)
                }
                onChangeHighlightingRules={(componentAssetAttributeId, highlightingRules) =>
                  props.onChangeHighlightingRules(componentAssetAttributeId, highlightingRules)
                }
                onRemoveAttribute={(componentAssetAttributeId) => props.onRemoveAttribute(componentAssetAttributeId)}
                onAddAttribute={() => props.onAddAttribute()}
                onReorderAttribute={(fromIndex, belowIndex) => props.onReorderAttribute(fromIndex, belowIndex)}
              />
            )}
          </div>
        </div>
        {props.inEditMode && props.isLastComponent && (
          <div className="d-flex position-absolute top-0 bottom-0" style={{ left: "100%" }}>
            <DroppableArea id={`after-${props.systemControlComponentId}`} isVertical />
          </div>
        )}
      </div>
      <RemoteOperationModal
        showModal={showRemoteOperationModal}
        assetId={props.assets.find((asset) => asset.assetName === props.assetName)?.assetId ?? null}
        onClose={() => setShowRemoteOperationModal(false)}
      />
    </div>
  );
}

interface Props {
  isLastComponent: boolean;
  systemControlComponentId: number;
  name: string;
  componentType: string;
  gauge: ComponentGauge | null;
  assets: Asset[];
  attributes: Attribute[];
  enumerationMap: EnumerationMap;
  assetName: string;
  remoteOperationEnabled: boolean;
  selectedAttributes: ComponentAttribute[];
  inEditMode: boolean;
  onChangeComponentName: (componentName: string) => void;
  onChangeComponentType: (componentType: string) => void;
  onChangeGaugeType: (gaugeType: string) => void;
  onChangeGaugeMin: (min: string) => void;
  onChangeGaugeMax: (max: string) => void;
  onChangeAssetName: (assetName: string) => void;
  onToggleRemoteOperationEnabled: () => void;
  onChangeAttribute: (componentAssetAttributeId: number, attributeName: string) => void;
  onChangeHighlightingRules: (componentAssetAttributeId: number, highlightingRules: HighlightingRule[]) => void;
  onRemoveAttribute: (componentAssetAttributeId: number) => void;
  onAddAttribute: () => void;
  onReorderAttribute: (fromIndex: number, belowIndex: number) => void;
  onDelete: () => void;
}

interface ComponentGauge {
  componentGaugeType: string;
  min: number | string;
  max: number | string;
}

interface Attribute {
  attributeId: number;
  regEnumId: number | null;
  name: string;
  code: string;
  unitShortName: string;
  unitLongName: string;
  isBoolean: boolean;
  isHighlightable: boolean;
}

interface EnumerationMap {
  [key: string]: EnumerationIndexMap;
}

interface EnumerationIndexMap {
  [key: string]: string;
}

interface ComponentAttribute {
  componentAssetAttributeId: number;
  attributeId: number;
  attributeName: string;
  assetId: number;
  assetName: string;
  unitShortName: string | null;
  unitLongName: string | null;
  regEnumId: number | null;
  currentValue: AttributeValue | null;
  graphValues: AttributeValue[];
  sortPriority: number;
  highlightingRules: HighlightingRule[];
}

interface AttributeValue {
  value: string | null;
  timestamp: string | null;
}

interface HighlightingRule {
  comparator: string;
  value: string;
  valueType: string;
  colorHexCode: string;
  sortPriority: number;
}

interface Asset {
  assetId: number;
  assetName: string;
  assetNickname: string;
  controllerModel: string | null;
  controllerName: string | null;
  deviceModel: string | null;
  deviceIdentifier: string | null;
  digitalOutputControls: DigitalOutputControl[];
  remoteOperationIsLocked: boolean;
  remoteOperationAllowed: boolean;
}

interface DigitalOutputControl {
  position: number;
  operationLabel: string;
  labelWhenTrue: string;
  labelWhenFalse: string;
}
